import {
  LiveAvatarApiClient,
  LiveAvatarLiteTokenResponse,
  LiveAvatarTelemetryEvent,
  LiveAvatarTrackingContext,
} from '../types';

const isLocalhost =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const API_BASE_URL = process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:5000' : '');
const LIVEAVATAR_MODE_STORAGE_KEY = 'liveavatar_mode';

export const isLiveAvatarDevMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.location.pathname === '/debug' || window.location.pathname.startsWith('/debug/')) {
    return true;
  }

  const queryMode = new URLSearchParams(window.location.search).get('mode');
  const pathModeMatch = window.location.pathname.match(/(?:^|\/)mode=(dev|prod)\/?$/);
  const mode = queryMode || pathModeMatch?.[1] || null;
  if (mode === 'dev') {
    window.sessionStorage.setItem(LIVEAVATAR_MODE_STORAGE_KEY, 'dev');
    return true;
  }
  if (mode === 'prod') {
    window.sessionStorage.removeItem(LIVEAVATAR_MODE_STORAGE_KEY);
    return false;
  }
  return window.sessionStorage.getItem(LIVEAVATAR_MODE_STORAGE_KEY) === 'dev';
};

const parseApiError = async (response: Response): Promise<string> => {
  try {
    const payload = await response.json();
    if (payload && typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error.trim();
    }
    if (payload && typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }
  } catch (_) {
    // ignored: fallback to status-based message below
  }

  return `Request failed (${response.status})`;
};

const requestJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const adminToken = typeof window !== 'undefined'
    ? window.sessionStorage.getItem('admin_token')
    : null;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
      ...(init?.headers || {}),
    },
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  return response.json() as Promise<T>;
};

export const createLiveAvatarApiClient = (): LiveAvatarApiClient => {
  return {
    async createLiteToken(context?: LiveAvatarTrackingContext): Promise<LiveAvatarLiteTokenResponse> {
      const sandbox = isLiveAvatarDevMode();
      return requestJson<LiveAvatarLiteTokenResponse>('/api/liveavatar/lite-token', {
        method: 'POST',
        body: JSON.stringify({ sandbox, ...(context || {}) }),
      });
    },

    async keepAlive(sessionToken: string): Promise<void> {
      await requestJson<{ success: boolean }>('/api/liveavatar/keep-alive', {
        method: 'POST',
        body: JSON.stringify({ session_token: sessionToken }),
      });
    },

    async stopSession(sessionToken: string): Promise<void> {
      await requestJson<{ success: boolean }>('/api/liveavatar/stop', {
        method: 'POST',
        body: JSON.stringify({ session_token: sessionToken }),
      });
    },

    async reportEvent(event: LiveAvatarTelemetryEvent): Promise<void> {
      await requestJson<{ success: boolean }>('/api/liveavatar/events', {
        method: 'POST',
        keepalive: true,
        body: JSON.stringify(event),
      });
    },
  };
};
